import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';

const articleImages: Record<string, string> = {
  'jd-618-super-discount-2026': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20lifestyle%20scene%20with%20warm%20golden%20tones%2C%20clean%20composition%2C%20soft%20natural%20lighting%2C%20elegant%20and%20simple%20aesthetic%2C%20high%20quality%20editorial%20photography&width=800&height=400&seq=art-jd618&orientation=landscape',
  'video-membership-comparison-2026': 'https://readdy.ai/api/search-image?query=Cozy%20living%20room%20with%20large%20TV%20screen%2C%20warm%20lighting%2C%20modern%20minimalist%20interior%2C%20comfortable%20sofa%2C%20entertainment%20setup%2C%20soft%20golden%20hour%20light%2C%20clean%20aesthetic&width=800&height=400&seq=art-video&orientation=landscape',
  'shenzhen-consumer-voucher-2026': 'https://readdy.ai/api/search-image?query=Bright%20modern%20cityscape%20of%20Shenzhen%20with%20warm%20sunset%20light%2C%20clean%20architecture%2C%20shopping%20district%2C%20vibrant%20urban%20scene%2C%20golden%20hour%20glow%2C%20elegant%20composition&width=800&height=400&seq=art-sz-voucher&orientation=landscape',
  'year-end-bonus-tax-planning': 'https://readdy.ai/api/search-image?query=Clean%20modern%20office%20desk%20with%20calculator%20and%20documents%2C%20warm%20natural%20lighting%2C%20professional%20minimalist%20workspace%2C%20soft%20golden%20tones%2C%20elegant%20composition&width=800&height=400&seq=art-tax&orientation=landscape',
  'airline-mileage-redemption-guide': 'https://readdy.ai/api/search-image?query=Airplane%20wing%20view%20above%20clouds%20at%20sunset%2C%20warm%20golden%20sky%2C%20travel%20inspiration%2C%20clean%20minimalist%20aerial%20view%2C%20peaceful%20atmosphere&width=800&height=400&seq=art-mileage&orientation=landscape',
  'online-course-certificate-subsidy': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20study%20desk%20with%20laptop%20and%20books%2C%20warm%20natural%20light%2C%20clean%20organized%20workspace%2C%20soft%20beige%20tones%2C%20elegant%20educational%20aesthetic&width=800&height=400&seq=art-course&orientation=landscape',
};

const defaultImage = 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20lifestyle%20scene%20with%20warm%20golden%20tones%2C%20clean%20composition%2C%20soft%20natural%20lighting%2C%20elegant%20aesthetic&width=800&height=400&seq=art-default&orientation=landscape';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeCat = categories.find((c) => c.slug === slug) || categories[0];
  const catArticles = getArticlesByCategory(activeCat.slug);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${activeCat.name} - 福利导航`,
            description: activeCat.description,
            url: `https://fulidaohang.com/category/${activeCat.slug}`,
          }),
        }}
      />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-8 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: activeCat.name },
              ]}
            />
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                    <i className={`${activeCat.icon} text-lg`}></i>
                  </span>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    {activeCat.name}
                  </h1>
                </div>
                <p className="text-sm text-foreground-500 max-w-2xl">
                  {activeCat.description}
                </p>
              </div>

              <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      cat.slug === activeCat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {catArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {catArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={articleImages[article.slug] || defaultImage}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        {article.hot && (
                          <div className="absolute top-3 left-3">
                            <span className="px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium">
                              热门
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs text-foreground-400">{article.date}</span>
                          <span className="text-xs text-foreground-400">·</span>
                          <span className="text-xs text-foreground-400">{article.readTime}</span>
                        </div>
                        <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-sm text-foreground-500">该分类暂无内容，敬请期待</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}